import type {
  ComplianceSummary,
  ConsentNoticeVersionSummary,
  PublicLookupData,
  SubjectAgeBand,
} from "@/lib/api/client"

export const REGISTRANT_RELATIONSHIP_OPTIONS = [
  { label: "Self", value: "self" },
  { label: "Parent or guardian", value: "parent_or_guardian" },
  { label: "Other family member", value: "other_family_member" },
  { label: "School or institution", value: "school_or_institution" },
  { label: "Employer or operator", value: "employer_or_operator" },
  { label: "Other", value: "other" },
] as const

export const GUARDIAN_RELATIONSHIP_OPTIONS = [
  { label: "Parent", value: "parent" },
  { label: "Legal guardian", value: "legal_guardian" },
  { label: "Grandparent", value: "grandparent" },
  { label: "Other family member", value: "other_family_member" },
  { label: "Caregiver", value: "caregiver" },
  { label: "Other", value: "other" },
] as const

export function getLookupCompliance(
  lookup: PublicLookupData | null
): ComplianceSummary | null {
  return lookup?.compliance ?? lookup?.registration?.compliance ?? null
}

export function isStudentsSafetyKit(lookup: PublicLookupData | null) {
  return lookup?.kit?.kitVariant === "students_safety_qr"
}

export function isGuardianRequiredRegistration(
  lookup: PublicLookupData | null,
  subjectAgeBand: SubjectAgeBand
) {
  const compliance = getLookupCompliance(lookup)

  return Boolean(
    compliance?.guardianRequired
      || compliance?.isMinor
      || isStudentsSafetyKit(lookup)
      || subjectAgeBand === "under_18"
  )
}

export function getActiveAdultNotice(
  lookup: PublicLookupData | null,
  fallbackNotice: ConsentNoticeVersionSummary | null
) {
  return getLookupCompliance(lookup)?.activeNoticeVersion ?? fallbackNotice
}

export function getNoticeSections(notice: ConsentNoticeVersionSummary | null) {
  const rawSections = notice?.contentJson?.sections

  if (!Array.isArray(rawSections)) {
    return []
  }

  return rawSections.flatMap((section) => {
    if (!section || typeof section !== "object") {
      return []
    }

    const heading =
      "heading" in section && typeof section.heading === "string"
        ? section.heading
        : ""
    const body =
      "body" in section && typeof section.body === "string" ? section.body : ""

    if (!heading && !body) {
      return []
    }

    return [{ body, heading }]
  })
}
